<script setup lang="ts">
import { onMounted, ref, reactive, computed, watch } from 'vue'
import { NButton, NEmpty, NFormItem, NInput, NSelect, NSpin, NTabPane, NTabs, useMessage } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import ListItemRow from '../components/ListItemRow.vue'
import CreateListModal from '../components/CreateListModal.vue'
import { useListItemsStore, type ListItem, type ListItemInput } from '../stores/lists'
import { useDocumentTitle } from '../composables/useDocumentTitle'

const store = useListItemsStore()
const message = useMessage()
useDocumentTitle('Lists')

const tab = ref<string>('')
const forms = reactive<Record<string, ListItemInput>>({})
const saving = reactive<Record<string, boolean>>({})
const editingItemId = ref<number | null>(null)
const draggingId = ref<number | null>(null)
const dragOverId = ref<number | null>(null)
const showCreateModal = ref(false)

// Bound rather than written in the template: vue-tsc reads the `//` of a
// literal URL in a component's props as a comment and loses the v-for scope.
const URL_PLACEHOLDER = 'https://…'

const PRIORITY_OPTIONS = [
  { label: 'High', value: 1 },
  { label: 'Medium', value: 2 },
  { label: 'Low', value: 3 },
]

function displayName(list: string): string {
  return list.replaceAll('_', ' ').replaceAll(/\b\w/g, (c) => c.toUpperCase())
}

function getForm(list: string): ListItemInput {
  if (!forms[list]) forms[list] = { list, title: '', url: '', notes: '', priority: null }
  return forms[list]
}

const editingList = computed<string | null>(() => {
  if (editingItemId.value === null) return null
  return store.items.find((i) => i.id === editingItemId.value)?.list ?? null
})

function listItemsFor(list: string): ListItem[] {
  const all = store.byList(list)
  return [...all.filter((i) => !i.done), ...all.filter((i) => i.done)]
}

// Set initial tab once items load
watch(
  () => store.listNames,
  (names) => {
    if (!tab.value && names.length > 0) tab.value = names[0]
  },
  { immediate: true },
)

onMounted(async () => {
  try {
    await store.fetchAll()
  } catch {
    /* loadError surfaces in the template */
  }
})

function cancelEdit(list: string) {
  editingItemId.value = null
  Object.assign(getForm(list), { title: '', url: '', notes: '', priority: null })
}

async function saveEdit(list: string) {
  const form = getForm(list)
  if (!form.title.trim()) {
    message.error('Title is required')
    return
  }
  if (editingItemId.value === null) return
  saving[list] = true
  try {
    await store.updateItem(editingItemId.value, {
      title: form.title,
      url: form.url || undefined,
      notes: form.notes || undefined,
      priority: form.priority || undefined,
    })
    cancelEdit(list)
    message.success('Item updated')
  } catch {
    message.error('Failed to update item')
  } finally {
    saving[list] = false
  }
}

async function addItem(list: string) {
  const form = getForm(list)
  if (!form.title.trim()) {
    message.error('Title is required')
    return
  }
  saving[list] = true
  try {
    await store.addItem({
      ...form,
      url: form.url || undefined,
      notes: form.notes || undefined,
      priority: form.priority || undefined,
    })
    Object.assign(form, { title: '', url: '', notes: '', priority: null })
    message.success('Item added')
  } catch {
    message.error('Failed to add item')
  } finally {
    saving[list] = false
  }
}

function submitForm(list: string) {
  return editingList.value === list ? saveEdit(list) : addItem(list)
}

async function startEditItem(item: ListItem) {
  const form = getForm(item.list)
  Object.assign(form, {
    list: item.list,
    title: item.title,
    url: item.url ?? '',
    notes: item.notes ?? '',
    priority: item.priority ?? null,
  })
  editingItemId.value = item.id
  tab.value = item.list
}

async function toggleDone(id: number, done: boolean) {
  try {
    await store.toggleDone(id, done)
  } catch {
    message.error('Failed to update item')
  }
}

async function deleteItem(id: number) {
  if (editingItemId.value === id) editingItemId.value = null
  try {
    await store.deleteItem(id)
  } catch {
    message.error('Failed to delete item')
  }
}

function onDragStart(event: DragEvent, id: number) {
  draggingId.value = id
  if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move'
}

function onDragOver(event: DragEvent, id: number) {
  event.preventDefault()
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
  if (draggingId.value !== id) dragOverId.value = id
}

function onDragEnd() {
  draggingId.value = null
  dragOverId.value = null
}

async function onDrop(event: DragEvent, list: string, targetId: number) {
  event.preventDefault()
  const fromId = draggingId.value
  draggingId.value = null
  dragOverId.value = null
  if (fromId === null || fromId === targetId) return
  await reorder(list, fromId, targetId)
}

async function moveItem(list: string, id: number, direction: 'up' | 'down') {
  const listItems = listItemsFor(list)
  const idx = listItems.findIndex((i) => i.id === id)
  const targetIdx = direction === 'up' ? idx - 1 : idx + 1
  if (targetIdx < 0 || targetIdx >= listItems.length) return
  await reorder(list, id, listItems[targetIdx].id)
}

async function reorder(list: string, fromId: number, toId: number) {
  const listItems = listItemsFor(list)
  const fromIdx = listItems.findIndex((i) => i.id === fromId)
  const toIdx = listItems.findIndex((i) => i.id === toId)
  if (fromIdx === -1 || toIdx === -1) return
  const reordered = [...listItems]
  const [moved] = reordered.splice(fromIdx, 1)
  reordered.splice(toIdx, 0, moved)
  try {
    await store.reorderItems(
      list,
      reordered.map((i) => i.id),
    )
  } catch {
    message.error('Failed to reorder items')
    await store.fetchAll()
  }
}
</script>

<template>
  <RouteShell>
    <section class="max-w-3xl mx-auto flex flex-col gap-4 pb-6">
      <header class="flex flex-col gap-1">
        <h1 class="text-2xl font-semibold text-white m-0">Lists</h1>
        <p class="text-sm text-white/60 m-0">Manage your lists.</p>
      </header>

      <div v-if="store.loading" class="flex items-center justify-center py-12">
        <NSpin aria-label="Loading…" />
      </div>
      <div v-else-if="store.loadError" class="flex flex-col items-center gap-3 py-12">
        <p class="text-red-400 text-sm m-0">{{ store.loadError }}</p>
        <NButton size="small" @click="store.fetchAll()">Try again</NButton>
      </div>

      <template v-else>
        <NTabs v-if="store.listNames.length > 0" v-model:value="tab" type="segment" animated>
          <template #suffix>
            <NButton size="small" quaternary @click="showCreateModal = true">New list</NButton>
          </template>
          <NTabPane v-for="list in store.listNames" :key="list" :name="list" :tab="displayName(list)">
            <div class="flex flex-col gap-3 mt-3">
              <div class="bg-white/5 rounded-lg p-4 flex flex-col gap-3">
                <p class="text-sm font-medium text-white/80 m-0">
                  {{ editingList === list ? 'Edit item' : `Add to ${displayName(list)}` }}
                </p>
                <NFormItem label="Title" :show-feedback="false">
                  <NInput
                    v-model:value="getForm(list).title"
                    :placeholder="`${displayName(list)} item…`"
                    @keydown.enter.prevent="submitForm(list)"
                  />
                </NFormItem>
                <NFormItem label="URL (optional)" :show-feedback="false">
                  <NInput v-model:value="getForm(list).url" :placeholder="URL_PLACEHOLDER" />
                </NFormItem>
                <NFormItem label="Notes (optional)" :show-feedback="false">
                  <NInput v-model:value="getForm(list).notes" type="textarea" :rows="2" placeholder="Any context…" />
                </NFormItem>
                <NFormItem label="Priority (optional)" :show-feedback="false">
                  <NSelect
                    v-model:value="getForm(list).priority"
                    :options="PRIORITY_OPTIONS"
                    clearable
                    placeholder="Unprioritized"
                  />
                </NFormItem>
                <div class="flex gap-2">
                  <NButton type="primary" :loading="!!saving[list]" @click="submitForm(list)">
                    {{ editingList === list ? 'Save changes' : `Add to ${displayName(list)}` }}
                  </NButton>
                  <NButton v-if="editingList === list" quaternary @click="cancelEdit(list)">Cancel</NButton>
                </div>
              </div>

              <NEmpty v-if="listItemsFor(list).length === 0" :description="`${displayName(list)} is empty`" />
              <ListItemRow
                v-for="(item, idx) in listItemsFor(list)"
                :key="item.id"
                :item="item"
                :is-editing="editingItemId === item.id"
                :dragging-id="draggingId"
                :drag-over-id="dragOverId"
                :is-first="idx === 0"
                :is-last="idx === listItemsFor(list).length - 1"
                @toggle="toggleDone"
                @edit="startEditItem"
                @delete="deleteItem"
                @dragstart="onDragStart"
                @dragover="onDragOver"
                @dragend="onDragEnd"
                @drop="(e, id) => onDrop(e, list, id)"
                @move-up="(id) => moveItem(list, id, 'up')"
                @move-down="(id) => moveItem(list, id, 'down')"
              />
            </div>
          </NTabPane>
        </NTabs>

        <NEmpty v-if="store.listNames.length === 0" description="No lists yet">
          <template #extra>
            <NButton type="primary" @click="showCreateModal = true">Create your first list</NButton>
          </template>
        </NEmpty>

        <CreateListModal v-model:show="showCreateModal" @created="tab = $event" />
      </template>
    </section>
  </RouteShell>
</template>
